/**
 * Pure, Pixi-free helpers for the building exterior scene.
 *
 * Keeping these in a separate module from ``BuildingExteriorScene.ts``
 * is what lets the vitest suite exercise them under a plain Node
 * environment -- the scene file imports ``pixi.js`` which reads
 * ``navigator`` at module-load time and crashes outside the browser.
 *
 * All math here is deterministic and side-effect-free; the scene file
 * re-exports the public helpers for compatibility with any external
 * consumers.
 */

import type { SceneFloor, ScenePod } from './SceneManager';

const FLOOR_ORDER = [
  'canary_lab',
  'active_offices',
  'reduced_floor',
  'frozen_basement',
] as const;

export interface LaidOutPod {
  podId: string;
  shortName: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Font size for this pod's label, picked to fit ``w``. */
  fontSize: number;
}

export interface LaidOutFloor {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Font size for the floor's label. */
  labelFontSize: number;
  /**
   * Where the label anchors inside the floor row.
   * ``inset-left`` = left margin (wide layout, vertically centered);
   * ``top-left`` = inside the top padding (narrow layout, label sits
   * above the wrapping pod grid).
   */
  labelAnchor: 'inset-left' | 'top-left';
  pods: LaidOutPod[];
}

export interface BuildingLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  floors: LaidOutFloor[];
}

/**
 * Compute everything pixel-positioned for a single scene rebuild.  Two
 * layout strategies:
 *
 *   wide   (>= 600 px) — fixed 4 equal-height rows, floor label sits to
 *                        the left, pods stretch across the right area
 *                        in a single row.
 *   narrow (<  600 px) — variable-height rows, floor label sits on top,
 *                        pods wrap into a multi-column grid below so a
 *                        floor with 5+ pods stays readable on phones.
 */
export function layoutBuilding({
  width,
  height,
  floors,
}: {
  width: number;
  height: number;
  floors: SceneFloor[];
}): BuildingLayout {
  const isNarrow = width < 600;
  const margin = Math.max(isNarrow ? 12 : 24, Math.min(width, height) * 0.04);
  const w = width - margin * 2;
  const x = margin;

  const orderedFloors = FLOOR_ORDER.map((id) =>
    floors.find((f) => f.id === id),
  ).filter((f): f is SceneFloor => Boolean(f));

  if (isNarrow) {
    return layoutBuildingNarrow({ x, w, height, margin, orderedFloors });
  }
  return layoutBuildingWide({ x, w, height, margin, orderedFloors });
}

function layoutBuildingWide({
  x,
  w,
  height,
  margin,
  orderedFloors,
}: {
  x: number;
  w: number;
  height: number;
  margin: number;
  orderedFloors: SceneFloor[];
}): BuildingLayout {
  const y = margin;
  const h = height - margin * 2;
  const floorCount = Math.max(1, orderedFloors.length);
  const floorGap = 12;
  const floorHeight = (h - floorGap * (floorCount - 1)) / floorCount;

  return {
    x,
    y,
    w,
    h,
    floors: orderedFloors.map((floor, idx) => {
      const floorY = y + idx * (floorHeight + floorGap);
      const labelReserve = 140;
      const podsArea = w - labelReserve - 24;
      const podCount = floor.pods.length;
      const podGap = 14;
      // Cap pod width so a single-pod floor doesn't get an absurdly
      // wide window; cap pod height proportionally to the floor so
      // tall canvases give bigger, more readable pod windows.
      const podWidth =
        podCount > 0
          ? Math.min(190, (podsArea - podGap * (podCount - 1)) / podCount)
          : 0;
      // Take 60% of the floor height (clamped) so pods get visual
      // presence on tall desktop canvases without becoming square.
      const podHeight = Math.min(
        110,
        Math.max(56, floorHeight * 0.6),
      );
      const startX = x + labelReserve + 12;
      const startY = floorY + (floorHeight - podHeight) / 2;
      const podFontSize = podFontFor(podWidth);
      return {
        id: floor.id,
        label: floor.label,
        x,
        y: floorY,
        w,
        h: floorHeight,
        // Cap the floor label so it stops growing into a giant block
        // when the canvas is tall.  18 px reads well at most widths.
        labelFontSize: Math.min(18, Math.max(13, Math.round(floorHeight * 0.10))),
        labelAnchor: 'inset-left' as const,
        pods: floor.pods.map((pod, podIdx) => ({
          podId: pod.podId,
          shortName: shortPodLabel(pod, maxCharsFor(podWidth, podFontSize)),
          x: startX + podIdx * (podWidth + podGap),
          y: startY,
          w: podWidth,
          h: podHeight,
          fontSize: podFontSize,
        })),
      };
    }),
  };
}

function layoutBuildingNarrow({
  x,
  w,
  height,
  margin,
  orderedFloors,
}: {
  x: number;
  w: number;
  height: number;
  margin: number;
  orderedFloors: SceneFloor[];
}): BuildingLayout {
  const labelHeight = 20;
  const innerPadX = 10;
  const innerPadTop = 4;
  const innerPadBottom = 8;
  const podGap = 6;
  const floorGap = 8;

  // Column count scales with viewport: 4 if there's room, otherwise 3 or 2.
  const cols = w >= 480 ? 4 : w >= 340 ? 3 : 2;
  const podWidth = Math.max(54, (w - 2 * innerPadX - podGap * (cols - 1)) / cols);
  const basePodHeight = 40;

  // First pass: how tall would each floor want to be?
  const wanted = orderedFloors.map((floor) => {
    const rows = Math.max(1, Math.ceil(Math.max(1, floor.pods.length) / cols));
    return (
      labelHeight + innerPadTop + rows * basePodHeight + (rows - 1) * podGap + innerPadBottom
    );
  });
  const totalGap = floorGap * Math.max(0, orderedFloors.length - 1);
  const totalAvail = height - margin * 2 - totalGap;
  const totalWanted = wanted.reduce((a, b) => a + b, 0);

  // If the floors don't fit, shrink only the pod-row portion proportionally
  // (keeping label + padding intact) down to a 60% floor.
  let podHeight = basePodHeight;
  if (totalWanted > totalAvail) {
    const overheadPerFloor = labelHeight + innerPadTop + innerPadBottom;
    const totalOverhead = overheadPerFloor * orderedFloors.length;
    const flexAvail = Math.max(0, totalAvail - totalOverhead);
    const flexWanted = Math.max(1, totalWanted - totalOverhead);
    const ratio = Math.max(0.6, flexAvail / flexWanted);
    podHeight = basePodHeight * ratio;
  }

  let cursorY = margin;
  const laidOut = orderedFloors.map((floor) => {
    const rows = Math.max(1, Math.ceil(Math.max(1, floor.pods.length) / cols));
    const fh =
      labelHeight + innerPadTop + rows * podHeight + (rows - 1) * podGap + innerPadBottom;
    const startX = x + innerPadX;
    const startY = cursorY + labelHeight + innerPadTop;
    const podFontSize = podFontFor(podWidth);
    const pods: LaidOutPod[] = floor.pods.map((pod, podIdx) => {
      const row = Math.floor(podIdx / cols);
      const col = podIdx % cols;
      return {
        podId: pod.podId,
        shortName: shortPodLabel(pod, maxCharsFor(podWidth, podFontSize)),
        x: startX + col * (podWidth + podGap),
        y: startY + row * (podHeight + podGap),
        w: podWidth,
        h: podHeight,
        fontSize: podFontSize,
      };
    });
    const floorRow: LaidOutFloor = {
      id: floor.id,
      label: floor.label,
      x,
      y: cursorY,
      w,
      h: fh,
      labelFontSize: 12,
      labelAnchor: 'top-left',
      pods,
    };
    cursorY += fh + floorGap;
    return floorRow;
  });

  return {
    x,
    y: margin,
    w,
    h: Math.max(0, cursorY - margin - floorGap),
    floors: laidOut,
  };
}

export function podFontFor(podWidth: number): number {
  if (podWidth >= 100) return 11;
  if (podWidth >= 70) return 10;
  return 9;
}

/** How many characters fit horizontally before a label overflows the pod box. */
export function maxCharsFor(podWidth: number, fontSize: number): number {
  // Conservative average glyph width for the system stack at the given
  // font size; gives a chars budget the truncator can target.
  const avgGlyphWidth = fontSize * 0.58;
  const usable = Math.max(8, podWidth - 8);
  return Math.max(3, Math.floor(usable / avgGlyphWidth));
}

export function shortPodLabel(pod: ScenePod, maxChars = 16): string {
  const name = pod.name ?? '';
  if (name && name.length <= maxChars) return name;
  // Drop common prefix to keep windows readable.
  const trimmed = (name || pod.podId).replace(/^pod_moex_/, '');
  if (trimmed.length <= maxChars) return trimmed;
  if (maxChars <= 3) return trimmed.slice(0, maxChars);
  return trimmed.slice(0, maxChars - 1) + '…';
}

/**
 * Compact, deterministic key whose change forces a full scene rebuild.
 */
export function signatureOf(floors: SceneFloor[]): string {
  return floors
    .map(
      (f) =>
        `${f.id}:${f.pods
          .map((p) => `${p.podId}|${p.riskState}|${p.isCanary ? 1 : 0}`)
          .join(',')}`,
    )
    .join(';');
}

/**
 * Cheap deterministic seed from a pod id (FNV-1a 32-bit).  Different pods
 * get different ice-crack patterns; the same pod keeps the same pattern
 * across frames so the cracks don't shimmer.
 */
export function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** xorshift32 PRNG returning ``[0, 1)``. */
export function mulberry32(state: number): () => number {
  let s = state || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
